import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Sparkles } from '../../components/CustomIcons';
import Pagination from '../../components/Pagination';
import { postRequest } from "../../utils/networking";
import { apiUrls } from "../../utils/url";
import NoDataFound from '../../components/NoDataFound';
import CommonToastContainer from '../../components/CommonToastContainer';
import ServiceLogsPopup from '../../components/ServiceLogsPopup';
import UpdateStatusModal from '../../components/UpdateStatusModal'; 

const getServiceTypeName = (servicetype) => {
  const serviceTypes = {
    0: 'Seller Account Setup',
    1: 'GST Registration and Filing',
    2: 'E-commerce Website',
    3: 'Product Listing Creation',
    4: 'PPC Campaign Management ',
    5: 'Trademark Registration',
    6: 'Seller Account Auditing'
  };
  return serviceTypes[servicetype] || `Service Type ${servicetype}`;
};

const getStatusBadgeClasses = (currentstatus) => {
  const status = Number(currentstatus);
  switch (status) {
    case 0:
      return 'bg-amber-50 text-amber-700';
    case 1:
      return 'bg-sky-50 text-sky-700';
    case 2:
      return 'bg-emerald-50 text-emerald-700';
    case 3:
      return 'bg-blue-50 text-blue-700';
    case 4:
      return 'bg-purple-50 text-purple-700';
    default:
      return 'bg-gray-50 text-gray-700';
  }
};

const getStatusText = (currentstatus) => {
  const status = Number(currentstatus);
  switch (status) {
    case 0:
      return 'Pending';
    case 1:
      return 'In Progress';
    case 2:
      return 'Completed';
    case 4:
      return 'New';
    default:
      return 'Pending';
  }
};

const getStatusIcon = (currentstatus) => {
  const status = Number(currentstatus);
  switch (status) {
    case 0:
      return '⏳';
    case 1:
      return '🔄';
    case 2:
      return '✅';
    case 4:
      return <Sparkles size={16} className="inline-block -mt-0.5" />;
    default:
      return '⏳';
  }
};

const FilterDropdown = ({ currentFilter, onFilterChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filters = [
    { value: 'all', label: 'All Status', color: 'bg-gray-500' },
    { value: 4, label: 'New', color: 'bg-purple-600' },
    { value: 0, label: 'Pending', color: 'bg-amber-600' },
    { value: 1, label: 'In Progress', color: 'bg-sky-600' },
    { value: 3, label: 'Re-Opened', color: 'bg-blue-600' },
    { value: 2, label: 'Completed', color: 'bg-emerald-600' },
  ];

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white border border-zinc-200 text-zinc-700 px-5 py-2 rounded-xl text-sm font-medium  flex items-center gap-2"
      >
        Filter by Status
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white min-w-[200px] rounded-xl py-2 z-50 border border-gray-200 shadow-lg">
          {filters.map((filter) => (
            <div
              key={filter.value}
              className={`px-5 py-2.5 cursor-pointer text-sm flex items-center gap-3 transition-colors ${
                currentFilter === filter.value 
                  ? 'bg-amber-50 text-amber-800 font-medium' 
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => {
                onFilterChange(filter.value);
                setIsOpen(false);
              }}
            >
              <span className={`w-2 h-2 rounded-full inline-block ${filter.color}`}></span>
              <span>{filter.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Global Action Dropdown
const GlobalActionDropdown = ({ 
  isOpen, 
  onClose, 
  position, 
  currentStatus, 
  onStatusChangeRequest,
  rowRcid,
  isUpdating 
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const statusOptions = [
    { value: 4, label: 'New', icon: <Sparkles size={16} />, theme: 'text-purple-700 hover:bg-purple-50', activeTheme: 'bg-purple-100 font-medium' },
    { value: 0, label: 'Pending', icon: '⏳', theme: 'text-amber-700 hover:bg-amber-50', activeTheme: 'bg-amber-100 font-medium' },
    { value: 1, label: 'In Progress', icon: '🔄', theme: 'text-sky-700 hover:bg-sky-50', activeTheme: 'bg-sky-100 font-medium' },
    { value: 3, label: 'Re-Opened', icon: '🔓', theme: 'text-blue-700 hover:bg-blue-50', activeTheme: 'bg-blue-100 font-medium' },
    { value: 2, label: 'Completed', icon: '✅', theme: 'text-emerald-700 hover:bg-emerald-50', activeTheme: 'bg-emerald-100 font-medium' },
  ];

  const handleStatusClick = (value) => {
    if (currentStatus === value) {
      onClose();
      return;
    }
    onClose();
    onStatusChangeRequest(rowRcid, value);
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[150] bg-transparent"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      
      {/* Dropdown */}
      <div
        ref={dropdownRef}
        className="fixed z-[151] bg-white min-w-[150px] rounded-lg py-1 border border-gray-200 shadow-lg"
        style={{
          top: position?.top + (position?.height || 0) + 5,
          left: position?.left,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isUpdating ? (
          <div className="px-4 py-2 text-center">
            <div className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-solid border-amber-600 border-r-transparent"></div>
            <span className="ml-2 text-sm">Updating...</span>
          </div>
        ) : (
          statusOptions.map((option) => (
            <div
              key={option.value}
              className={`px-4 py-2 mx-1 my-1 rounded-md cursor-pointer text-sm flex items-center gap-2 transition-colors ${option.theme} ${
                currentStatus === option.value ? option.activeTheme : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusClick(option.value);
              }}
            >
              <span>{option.icon}</span> 
              <span>{option.label}</span>
            </div>
          ))
        )}
      </div>
    </>
  );
};

const TableRow = React.memo(({ 
  registration, 
  index, 
  isSelected, 
  onRowClick, 
  onStatusChangeRequest,
  onOpenDropdown,
  activeDropdownId,
  dropdownPosition,
  isUpdating 
}) => {
  const currentStatusValue = Number(registration.currentstatus);
  const statusBadgeClasses = getStatusBadgeClasses(currentStatusValue);
  const statusText = getStatusText(currentStatusValue);
  const statusIcon = getStatusIcon(currentStatusValue);
  
  const serviceName = getServiceTypeName(registration.servicetype);
  const actionButtonRef = useRef(null);

  const handleActionClick = (e) => {
    e.stopPropagation();
    const rect = actionButtonRef.current?.getBoundingClientRect();
    onOpenDropdown(registration.rcid, {
      top: rect?.top || 0,
      left: rect?.left || 0,
      height: rect?.height || 0
    });
  };

  return (
    <div 
      className={`grid grid-cols-[60px_1.2fr_1.2fr_1.8fr_1.2fr_1fr_0.8fr] min-w-[1000px] gap-3 py-4 px-6 transition-all duration-200 border-b border-zinc-100 cursor-pointer ${
        isSelected ? 'bg-blue-50/50 hover:bg-blue-50/70' : 'hover:bg-zinc-100'
      }`}
      onClick={onRowClick}
    >
      <div className="flex items-center">
        <span className="text-sm font-semibold text-zinc-800">
          {index + 1}
        </span>
      </div>
      
      <div className="flex items-center min-w-0">
        <span className="font-medium text-sm text-zinc-900 truncate" title={serviceName}>
          {serviceName}
        </span>
      </div>
      
      <div className="flex items-center min-w-0">
        <span className="font-medium text-sm text-zinc-900 truncate" title={registration.name || registration.username}>
          {registration.name || registration.username || '-'}
        </span>
      </div>
      
      <div className="flex items-center min-w-0">
        <span className="text-sm text-zinc-900 truncate" title={registration.email}>
          {registration.email || '-'}
        </span>
      </div>
      
      <div className="flex items-center">
        <span className="text-sm text-zinc-700 font-mono">{registration.phonenumber || '-'}</span>
      </div>
      
      <div className="flex items-center">
        <span className={`status-badge px-2.5 py-1 rounded-full text-xs font-semibold inline-block whitespace-nowrap ${statusBadgeClasses}`}>
          {statusIcon} {statusText}
        </span>
      </div>
      
      <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        <button
          ref={actionButtonRef}
          onClick={handleActionClick}
          disabled={isUpdating}
          className="bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full text-sm font-medium  flex items-center gap-1 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUpdating ? (
            <>
              <div className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-solid border-amber-600 border-r-transparent"></div>
              <span>Updating...</span>
            </>
          ) : (
            <>
              Action
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </>
          )}
        </button>
      </div>

      <GlobalActionDropdown
        isOpen={activeDropdownId === registration.rcid}
        onClose={() => onOpenDropdown(null, null)}
        position={dropdownPosition}
        currentStatus={currentStatusValue}
        onStatusChangeRequest={onStatusChangeRequest}
        rowRcid={registration.rcid}
        isUpdating={isUpdating && activeDropdownId === registration.rcid}
      />
    </div>
  );
});

const Service = () => {
  const [registrations, setRegistrations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [updatingRcids, setUpdatingRcids] = useState(new Set());
  
  // State for selected rows
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // State for delete confirmation popup
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deletingRcid, setDeletingRcid] = useState(null);
  
  // State for global dropdown
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, height: 0 });

  // State for Service Logs Popup
  const [showLogsPopup, setShowLogsPopup] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [isLogsLoading, setIsLogsLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // State for Status Update Modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatusData, setSelectedStatusData] = useState({ rcid: null, status: null, newStatus: null });

  const fetchServiceLogs = async (registration) => {
    setSelectedService(registration);
    setShowLogsPopup(true);
    setIsLogsLoading(true);
    try {
      const response = await postRequest(apiUrls.getservicelogs, { rcid: registration.rcid });
      console.log("Service logs response:", response);
      
      if (response.status === "S" && response.data) {
        setSelectedLogs(Array.isArray(response.data) ? response.data : []);
      } else {
        setSelectedLogs([]);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Failed to load activity logs");
      setSelectedLogs([]);
    } finally {
      setIsLogsLoading(false);
    }
  };

  const fetchServiceRegistrations = async () => {
    setIsLoading(true);
    try {
      const response = await postRequest(apiUrls.getincompleteservice, {});
      
      console.log("Service registrations response:", response);
      
      if (response.status === "S" && response.data && Array.isArray(response.data)) {
        setRegistrations(response.data);
      } else if (response.status === "E") {
        toast.error(response.message || "Failed to load service registrations");
        setRegistrations([]);
      } else {
        setRegistrations([]);
      }
    } catch (error) {
      console.error("Error fetching service registrations:", error);
      toast.error("Unable to load service registrations. Please try again.");
      setRegistrations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const updateRegistrationStatus = async (rcid, newStatus, remark) => {
    try {
      const requestPayload = {
        rcid: rcid,
        givenstatus: Number(newStatus),
        description: remark  
      };
      
      console.log(`Updating status for RCID: ${rcid} with payload:`, requestPayload);
      
      const response = await postRequest(apiUrls.updateserviceregister, requestPayload);
      
      console.log("Update status response:", response);
      
      if (response.status === "S") {
        toast.success(response.message || `Status updated to ${getStatusText(newStatus)} successfully!`);
        await fetchServiceRegistrations();
        return true;
      } else {
        toast.error(response.message || "Failed to update status");
        return false;
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Unable to update status. Please try again.");
      return false;
    }
  };

  const handleStatusChangeRequest = (rcid, newStatus) => {
    const registration = registrations.find(reg => reg.rcid === rcid);
    if (!registration) {
      toast.error("Registration not found");
      return;
    }
    
    setSelectedStatusData({
      rcid: rcid,
      status: registration.currentstatus,
      newStatus: newStatus
    });
    setShowStatusModal(true);
    handleCloseDropdown();
  };

  const handleStatusUpdate = async (rcid, newStatus, remark) => {
    if (!rcid || newStatus === null) {
      toast.error("Invalid update request");
      return;
    }

    const newStatusNumber = Number(newStatus);
    const registration = registrations.find(reg => reg.rcid === rcid);
    if (!registration) {
      toast.error("Registration not found");
      setShowStatusModal(false);
      return;
    }
    
    setUpdatingRcids(prev => new Set(prev).add(rcid));
    
    // Optimistic update
    const previousRegistrations = [...registrations];
    setRegistrations(prevState => 
      prevState.map(reg => 
        reg.rcid === rcid ? { ...reg, currentstatus: newStatusNumber } : reg
      )
    );
    
    const success = await updateRegistrationStatus(rcid, newStatusNumber, remark);
    
    setUpdatingRcids(prev => {
      const newSet = new Set(prev);
      newSet.delete(rcid);
      return newSet;
    });
    
    if (!success) {
      // Revert on failure
      setRegistrations(previousRegistrations);
    }
    
    // Close modal and clear selected data
    setShowStatusModal(false);
    setSelectedStatusData({ rcid: null, status: null, newStatus: null });
  };

  // Handle row selection
  const handleRowSelect = (rcid) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rcid)) {
      newSelected.delete(rcid);
    } else {
      newSelected.add(rcid);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
    } else {
      const allIds = currentRegistrations.map(reg => reg.rcid);
      setSelectedRows(new Set(allIds));
    }
    setSelectAll(!selectAll);
  };

  // Handle bulk status update for selected rows
  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedRows.size === 0) {
      toast.warning("Please select at least one service");
      return;
    }

    const loadingToast = toast.loading(`Updating ${selectedRows.size} service(s)...`);
    
    let successCount = 0;
    let failCount = 0;

    for (const rcid of selectedRows) {
      const success = await updateRegistrationStatus(rcid, newStatus, "Bulk update");
      if (success) {
        successCount++;
        setRegistrations(prev => 
          prev.map(reg => 
            reg.rcid === rcid ? { ...reg, currentstatus: newStatus } : reg
          )
        );
      } else {
        failCount++;
      }
    }

    toast.dismiss(loadingToast);
    
    if (successCount > 0) {
      toast.success(`Updated ${successCount} service(s) to ${getStatusText(newStatus)}`);
      setSelectedRows(new Set());
      setSelectAll(false);
    }
    if (failCount > 0) {
      toast.error(`Failed to update ${failCount} service(s)`);
    }
  };

  const handleOpenDropdown = (rcid, position) => {
    setActiveDropdownId(rcid);
    if (position) {
      setDropdownPosition(position);
    }
  };

  const handleCloseDropdown = () => {
    setActiveDropdownId(null);
    setDropdownPosition({ top: 0, left: 0, height: 0 });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    setSelectedRows(new Set());
    setSelectAll(false);
    handleCloseDropdown();
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setSelectedRows(new Set());
    setSelectAll(false);
    handleCloseDropdown();
  };

  useEffect(() => {
    fetchServiceRegistrations();
  }, []);

  const filteredRegistrations = registrations.filter(reg => {
    if (filter !== 'all' && reg.currentstatus !== filter) return false;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const serviceTypeName = getServiceTypeName(reg.servicetype).toLowerCase();
      const statusText = getStatusText(reg.currentstatus).toLowerCase();
      return (reg.username && reg.username.toLowerCase().includes(searchLower)) || 
             (reg.email && reg.email.toLowerCase().includes(searchLower)) ||
             (reg.name && reg.name.toLowerCase().includes(searchLower)) ||
             (reg.phonenumber && reg.phonenumber.toLowerCase().includes(searchLower)) ||
             serviceTypeName.includes(searchLower) ||
             statusText.includes(searchLower);
    }
    
    return true;
  });

  const totalItems = filteredRegistrations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRegistrations = filteredRegistrations.slice(startIndex, endIndex);

  // Update selectAll when current page data changes
  useEffect(() => {
    const currentIds = currentRegistrations.map(reg => reg.rcid);
    const allSelected = currentIds.length > 0 && currentIds.every(id => selectedRows.has(id));
    setSelectAll(allSelected);
  }, [selectedRows, currentRegistrations]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRows(new Set());
    setSelectAll(false);
    handleCloseDropdown();
    const tableContainer = document.querySelector('.table-scroll-container');
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setSelectedRows(new Set());
    setSelectAll(false);
    handleCloseDropdown();
  };

  useEffect(() => {
    setCurrentPage(1);
    setSelectedRows(new Set());
    setSelectAll(false);
    handleCloseDropdown();
  }, [filter, searchTerm]);

  const deletingService = registrations.find(reg => reg.rcid === deletingRcid);

  const handleCancelDelete = () => {
    setShowDeletePopup(false);
    setDeletingRcid(null);
  };

  const handleConfirmDelete = async () => {
    // Add your delete logic here
    setShowDeletePopup(false);
    setDeletingRcid(null);
  };

  return (
    <div className="bg-[#F8F9FE] text-zinc-900 antialiased h-full overflow-hidden">
      <main className="max-w-[1400px] mx-auto h-full flex flex-col p-2 sm:p-3 lg:px-4 lg:py-6 w-full">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-80">
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-4 py-2 pl-10 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
            <svg 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <FilterDropdown currentFilter={filter} onFilterChange={setFilter} />
        </div>
      
        <div className="bg-white/70 rounded-xl border border-zinc-100 shadow-sm flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="overflow-auto flex-1 flex flex-col min-h-0">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-zinc-100 to-zinc-50 border-b border-zinc-200 min-w-[1000px]">
              <div className="grid grid-cols-[60px_1.2fr_1.2fr_1.8fr_1.2fr_1fr_0.8fr] gap-3 px-6 py-3 text-sm font-semibold text-zinc-600">
                <div className="whitespace-nowrap">S.No</div>
                <div className="whitespace-nowrap">Service Name</div>
                <div className="whitespace-nowrap">User Name</div>
                <div className="whitespace-nowrap">User Email</div>
                <div className="whitespace-nowrap">Phone</div>
                <div className="whitespace-nowrap">Status</div>
                <div className="whitespace-nowrap text-center">Action</div>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="table-scroll-container flex-1 relative min-h-[400px]">
              {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-amber-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                </div>
              ) : currentRegistrations.length > 0 ? (
                currentRegistrations.map((registration, index) => (
                  <TableRow
                    key={registration.rcid}
                    registration={registration}
                    index={startIndex + index}
                    isSelected={selectedRows.has(registration.rcid)}
                    onRowClick={() => fetchServiceLogs(registration)}
                    onStatusChangeRequest={handleStatusChangeRequest}
                    onOpenDropdown={handleOpenDropdown}
                    activeDropdownId={activeDropdownId}
                    dropdownPosition={dropdownPosition}
                    isUpdating={updatingRcids.has(registration.rcid)}
                  />
                ))
              ) : (
                <NoDataFound searchTerm={searchTerm} />
              )}
            </div>
          </div>

          {totalItems > 0 && !isLoading && (
            <div className="mt-auto border-t border-zinc-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </main>

      {/* Service Logs Popup */}
      <ServiceLogsPopup 
        isOpen={showLogsPopup}
        onClose={() => setShowLogsPopup(false)}
        logs={selectedLogs}
        isLoading={isLogsLoading}
        serviceName={selectedService ? getServiceTypeName(selectedService.servicetype) : ''}
        userName={selectedService ? (selectedService.name || selectedService.username) : ''}
      />
      
      {/* Status Update Modal */}
     <UpdateStatusModal
  isOpen={showStatusModal}
  onClose={() => {
    setShowStatusModal(false);
    setSelectedStatusData({ rcid: null, status: null, newStatus: null });
  }}
  data={{
    rcid: selectedStatusData.rcid,
    newStatus: selectedStatusData.newStatus  // Only passing the new status
  }}
  onUpdate={handleStatusUpdate}
  isUpdating={selectedStatusData.rcid ? updatingRcids.has(selectedStatusData.rcid) : false}
/>
      {/* Delete Confirmation Popup */}
      {showDeletePopup && deletingService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-slate-800 mb-2">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete the service <span className="font-semibold">"{deletingService.username}"</span>?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm font-medium text-slate-600  rounded-lg "
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600  rounded-lg "
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      <CommonToastContainer />
    </div>
  );
};

export default Service;